import { subscribeSse } from '@/lib/sse'
import api from '@/lib/base-api'
import { AUTH_LOGOUT_STORAGE_KEY, endSession } from '@/lib/auth-session'
import { useAiModelStore } from '@/store/useAiModelStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

interface RealtimeEvent {
  resource: 'session' | 'conversation' | 'models' | 'agents' | 'auth' | 'users'
  action?: string
  user_id?: number
  session_id?: number
  conversation_id?: number
}

const MAX_RECONNECT_DELAY_MS = 30_000

function isRealtimeEvent(value: unknown): value is RealtimeEvent {
  if (!value || typeof value !== 'object') return false
  return ['session', 'conversation', 'models', 'agents', 'auth', 'users'].includes(
    (value as { resource?: unknown }).resource as string,
  )
}

export function useRealtimeInvalidation() {
  const queryClient = useQueryClient()
  const authToken = useAuthStore((state) => state.authToken)

  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state, previous) => {
      if (state.sessionGeneration !== previous.sessionGeneration) {
        void queryClient.cancelQueries()
        queryClient.clear()
      }
    })
    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_LOGOUT_STORAGE_KEY || !event.newValue) return
      try {
        const token: unknown = JSON.parse(event.newValue)?.token
        endSession(false, typeof token === 'string' ? token : undefined)
      } catch {
        endSession(false)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => {
      unsubscribe()
      window.removeEventListener('storage', onStorage)
    }
  }, [queryClient])

  useEffect(() => {
    if (!authToken) return

    const controller = new AbortController()
    let reconnectDelay = 1_000
    let reconnectTimer: number | undefined

    const invalidate = (event: RealtimeEvent) => {
      switch (event.resource) {
        case 'auth':
          if (event.action === 'session_revoked') endSession(true, authToken)
          break
        case 'users':
          void queryClient.invalidateQueries({ queryKey: ['users'] })
          break
        case 'session':
          void queryClient.invalidateQueries({ queryKey: ['session-folders'] })
          void queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] })
          if (event.session_id != null) {
            void queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', event.session_id] })
            void queryClient.invalidateQueries({ queryKey: ['sessions', event.session_id] })
          }
          break
        case 'conversation':
          void queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] })
          if (event.session_id != null) {
            void queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', event.session_id] })
            void queryClient.invalidateQueries({ queryKey: ['sessions', event.session_id] })
          }
          if (event.conversation_id != null) {
            void queryClient.invalidateQueries({ queryKey: ['message-versions', event.conversation_id] })
          }
          break
        case 'agents':
          void queryClient.invalidateQueries({ queryKey: ['company-agents'] })
          break
        case 'models':
          void useAiModelStore
            .getState()
            .fetchModels()
            .catch(() => {})
          break
      }
    }

    const connect = async () => {
      try {
        await queryClient.invalidateQueries({ queryKey: ['whoami'] }, { cancelRefetch: false })
        if (controller.signal.aborted || !useAuthStore.getState().authToken) return
        const response = await api.getStream('/api/common/realtime/events', {
          signal: controller.signal,
        })
        if (!response.ok) throw new Error(`Realtime connection failed: ${response.status}`)

        reconnectDelay = 1_000
        await subscribeSse(response, {
          session_revoked: () => endSession(true, authToken),
          invalidate: (event) => {
            if (isRealtimeEvent(event)) invalidate(event)
          },
        })
      } catch (error) {
        if (controller.signal.aborted) return
        console.warn('Realtime connection interrupted.', error)
      }

      if (controller.signal.aborted) return
      reconnectTimer = window.setTimeout(() => {
        void connect()
      }, reconnectDelay)
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS)
    }

    void connect()
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.authToken !== authToken) controller.abort()
    })

    return () => {
      unsubscribe()
      controller.abort()
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer)
    }
  }, [authToken, queryClient])
}
